// ═══════════════════════════════════════════════════════════════════════════════
// MXDR365 — Task Drawer
// Portal-mounted right-side slide-over that opens whenever
// state.expandedProjectTask is set. Renders rich task detail (status, assignees,
// project, dates, labels, recurrence, watchers, description, subtasks,
// checklist, comments, activity, time, files) for both project-bound and
// standalone tasks. Source surfaces (Tasks, Board, Calendar, Workload,
// Timeline, Projects) opt in to up/down keyboard navigation by wrapping
// their content in TaskDrawerListProvider.
// ═══════════════════════════════════════════════════════════════════════════════
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { useApp } from '../../hooks/useAppState';
import {
  theme,
  statusColors,
  statusLabels,
  priorityColors,
  priorityIcons,
} from '../../lib/theme';
import { getUserName } from '../../lib/mockData';
import {
  getTaskAssigneeIds,
  isAssignedTo,
  normalizeAssigneeFields,
} from '../../lib/taskAssignees';
import {
  Avatar,
  AssigneePicker,
  AssigneeStack,
  Badge,
  Button,
  RecurrencePicker,
  TabButton,
} from './index';
import type {
  Subtask,
  Task,
  TaskPriority,
  TaskRecurrence,
  TaskStatus,
} from '../../types';

// ─── Sibling navigation context ──────────────────────────────────────────────
// Source surfaces expose a "next/previous task id given the current id" so the
// drawer's up/down arrows can step through the user's filtered list. Without
// a provider the arrows are hidden.
interface TaskDrawerListContextValue {
  nextTaskId: (currentId: string) => string | null;
  previousTaskId: (currentId: string) => string | null;
}

const TaskDrawerListContext = createContext<TaskDrawerListContextValue | null>(null);

export function TaskDrawerListProvider({
  taskIds,
  children,
}: {
  taskIds: string[];
  children: ReactNode;
}) {
  const value = useMemo<TaskDrawerListContextValue>(() => {
    const ids = taskIds.filter(Boolean);
    return {
      nextTaskId(currentId) {
        const idx = ids.indexOf(currentId);
        if (idx < 0 || idx >= ids.length - 1) return null;
        return ids[idx + 1];
      },
      previousTaskId(currentId) {
        const idx = ids.indexOf(currentId);
        if (idx <= 0) return null;
        return ids[idx - 1];
      },
    };
  }, [taskIds]);
  return (
    <TaskDrawerListContext.Provider value={value}>
      {children}
    </TaskDrawerListContext.Provider>
  );
}

// ─── Drawer root ─────────────────────────────────────────────────────────────
const STATUS_OPTIONS: TaskStatus[] = ['todo', 'progress', 'review', 'blocked', 'done', 'off-track'];
const PRIORITY_OPTIONS: TaskPriority[] = ['critical', 'high', 'medium', 'low'];
type DrawerTab = 'subtasks' | 'checklist' | 'comments' | 'activity' | 'time' | 'files';

export function TaskDrawer() {
  const { state, dispatch } = useApp();
  const taskId = state.expandedProjectTask;
  const siblings = useContext(TaskDrawerListContext);

  const allTasks = useMemo<Task[]>(() => {
    const projectTasks = state.projects.flatMap(p => p.tasks);
    return [...projectTasks, ...state.standaloneTasks];
  }, [state.projects, state.standaloneTasks]);

  const task = useMemo<Task | null>(
    () => (taskId ? allTasks.find(t => t.id === taskId) || null : null),
    [allTasks, taskId],
  );

  const close = useCallback(() => {
    dispatch({ type: 'SELECT_PROJECT_TASK', id: null });
  }, [dispatch]);

  // ESC closes; arrows step through the current filtered list when a provider
  // is mounted. Arrows skip if focus is in a text input so the user can edit
  // freely.
  useEffect(() => {
    if (!taskId) return;
    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      const editing =
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.tagName === 'SELECT' ||
          (target as HTMLElement).isContentEditable);
      if (e.key === 'Escape') {
        e.preventDefault();
        close();
        return;
      }
      if (editing) return;
      if (!siblings) return;
      if (e.key === 'ArrowDown') {
        const next = siblings.nextTaskId(taskId);
        if (next) {
          e.preventDefault();
          dispatch({ type: 'SELECT_PROJECT_TASK', id: next });
        }
      } else if (e.key === 'ArrowUp') {
        const prev = siblings.previousTaskId(taskId);
        if (prev) {
          e.preventDefault();
          dispatch({ type: 'SELECT_PROJECT_TASK', id: prev });
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [taskId, siblings, dispatch, close]);

  // Skeleton when an id is in state but the task isn't in the loaded slice yet
  // (deep-link race). After a brief grace period, fall back to closing and
  // toasting so the user isn't trapped in a perpetual empty drawer.
  const [graceExpired, setGraceExpired] = useState(false);
  useEffect(() => {
    if (!taskId) {
      setGraceExpired(false);
      return;
    }
    if (task) {
      setGraceExpired(false);
      return;
    }
    const handle = window.setTimeout(() => setGraceExpired(true), 2000);
    return () => window.clearTimeout(handle);
  }, [taskId, task]);

  useEffect(() => {
    if (graceExpired && taskId && !task) {
      dispatch({ type: 'TOAST', message: { type: 'info', message: 'Task not found — it may have been deleted.' } });
      dispatch({ type: 'SELECT_PROJECT_TASK', id: null });
    }
  }, [graceExpired, task, taskId, dispatch]);

  if (!taskId) return null;

  const content = task ? (
    <TaskDrawerBody task={task} allTasks={allTasks} onClose={close} siblings={siblings} />
  ) : (
    <DrawerSkeleton />
  );

  return createPortal(
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 1100,
        display: 'flex',
        justifyContent: 'flex-end',
        pointerEvents: 'auto',
      }}
    >
      <div
        onClick={close}
        style={{
          position: 'absolute',
          inset: 0,
          background: 'rgba(0,0,0,0.35)',
          animation: 'taskDrawerFadeIn .18s ease-out',
        }}
      />
      <div
        role="dialog"
        aria-label="Task detail"
        style={{
          position: 'relative',
          width: 'min(720px, 100vw)',
          height: '100%',
          background: theme.card,
          borderLeft: `1px solid ${theme.border}`,
          boxShadow: '-12px 0 32px rgba(0,0,0,0.45)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'taskDrawerSlideIn .2s ease-out',
        }}
      >
        {content}
      </div>
      <style>{`
        @keyframes taskDrawerSlideIn {
          from { transform: translateX(100%); }
          to { transform: translateX(0); }
        }
        @keyframes taskDrawerFadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
      `}</style>
    </div>,
    document.body,
  );
}

function DrawerSkeleton() {
  return (
    <div style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ height: 24, background: theme.input, borderRadius: 6, width: '70%' }} />
      <div style={{ height: 14, background: theme.input, borderRadius: 4, width: '40%' }} />
      <div style={{ height: 14, background: theme.input, borderRadius: 4, width: '90%' }} />
      <div style={{ height: 14, background: theme.input, borderRadius: 4, width: '60%' }} />
      <div style={{ fontSize: 11, color: theme.textTertiary, marginTop: 12 }}>Loading task…</div>
    </div>
  );
}

// ─── Drawer body ─────────────────────────────────────────────────────────────
function TaskDrawerBody({
  task,
  allTasks,
  onClose,
  siblings,
}: {
  task: Task;
  allTasks: Task[];
  onClose: () => void;
  siblings: TaskDrawerListContextValue | null;
}) {
  const { state, dispatch, canEditView } = useApp();
  const [tab, setTab] = useState<DrawerTab>('subtasks');
  const [titleDraft, setTitleDraft] = useState(task.title);
  const [descDraft, setDescDraft] = useState(task.description || '');
  const [commentDraft, setCommentDraft] = useState('');

  useEffect(() => { setTitleDraft(task.title); }, [task.id, task.title]);
  useEffect(() => { setDescDraft(task.description || ''); }, [task.id, task.description]);

  const project = task.projectId ? state.projects.find(p => p.id === task.projectId) : null;
  const projectId = task.projectId;
  const assigneeIds = getTaskAssigneeIds(task);

  // Permission model — aligned with the more-permissive Projects detail
  // panel. The drawer trusts view-level RBAC (canEditView) which is the
  // same gate ProjectsPage's TaskDetailPanel uses today. We additionally
  // permit creators / assignees / admins even when the user lacks the
  // task-view permission, because the legacy Tasks page rule excluded
  // creators and locked them out of their own tasks (see audit §3.4).
  // The first 'created' activity entry carries the creator id.
  const isCreator = useMemo(() => {
    const created = (task.activity || []).find(a => a.type === 'created');
    return !!state.user.id && created?.userId === state.user.id;
  }, [task.activity, state.user.id]);
  const canEdit = useMemo(() => {
    if (state.user.role === 'admin') return true;
    if (isCreator) return true;
    if (task.assigneeId === state.user.id) return true;
    if (isAssignedTo(task, state.user.id)) return true;
    // Fall back to view-level RBAC — if the user can edit the Tasks or
    // Projects view, they can edit a task they've drilled into. Matches
    // how ProjectsPage's inline detail panel behaves (no per-task gate).
    return canEditView('tasks') || canEditView('projects');
  }, [task, state.user.id, state.user.role, isCreator, canEditView]);

  // ─── Mutating helpers ─────────────────────────────────────────────────
  function patch(fields: Partial<Task>) {
    dispatch({ type: 'UPDATE_TASK', task: { id: task.id, projectId, ...fields } });
  }

  function commitTitle() {
    const next = titleDraft.trim();
    if (!next || next === task.title) return;
    patch({ title: next });
  }
  function commitDescription() {
    if (descDraft === (task.description || '')) return;
    patch({ description: descDraft });
  }

  function setStatus(next: TaskStatus) {
    patch({
      status: next,
      completedAt: next === 'done' ? new Date().toISOString() : null,
    });
  }
  function setPriority(next: TaskPriority) {
    patch({ priority: next });
  }
  function setAssignees(ids: string[]) {
    const norm = normalizeAssigneeFields(ids, state.user.id);
    patch({ assigneeId: norm.assigneeId, assigneeIds: norm.assigneeIds });
  }
  function setProject(nextProjectId: string | null) {
    patch({ projectId: nextProjectId });
  }
  function setDueDate(v: string) { patch({ dueDate: v || null }); }
  function setStartDate(v: string) { patch({ startDate: v || null }); }
  function setRecurrence(rec: TaskRecurrence | null) { patch({ recurrence: rec }); }
  function setMilestone(v: boolean) { patch({ isMilestone: v }); }
  function setEstimate(v: string) {
    const n = parseFloat(v);
    patch({ estimatedHours: isNaN(n) ? 0 : n });
  }
  function setStoryPoints(v: string) {
    const n = parseFloat(v);
    patch({ storyPoints: isNaN(n) ? 0 : n });
  }

  function addLabel(label: string) {
    const trimmed = label.trim();
    if (!trimmed) return;
    if ((task.labels || []).includes(trimmed)) return;
    patch({ labels: [...(task.labels || []), trimmed] });
  }
  function removeLabel(label: string) {
    patch({ labels: (task.labels || []).filter(l => l !== label) });
  }

  function toggleWatcher() {
    dispatch({
      type: 'TOGGLE_TASK_WATCHER',
      taskId: task.id,
      projectId,
      userId: state.user.id,
    });
  }
  function postComment() {
    if (!commentDraft.trim()) return;
    dispatch({
      type: 'ADD_TASK_COMMENT',
      taskId: task.id,
      projectId,
      comment: commentDraft,
    });
    setCommentDraft('');
  }
  function deleteTask() {
    if (!confirm(`Delete "${task.title}"?`)) return;
    dispatch({ type: 'DELETE_TASK', projectId, taskId: task.id });
    onClose();
  }

  // Subtasks (embedded — Planner-style)
  const embeddedSubtasks = useMemo(
    () => (task.subtasks || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0)),
    [task.subtasks],
  );

  function persistSubtasks(next: Subtask[]) {
    patch({ subtasks: next.map((s, i) => ({ ...s, order: i })) });
  }
  function addEmbeddedSubtask(title: string) {
    const trimmed = title.trim();
    if (!trimmed) return;
    persistSubtasks([
      ...embeddedSubtasks,
      { id: `st-${Date.now()}`, title: trimmed, completed: false, order: embeddedSubtasks.length },
    ]);
  }
  function toggleSubtask(id: string) {
    persistSubtasks(embeddedSubtasks.map(s => s.id === id ? { ...s, completed: !s.completed } : s));
  }
  function renameSubtask(id: string, title: string) {
    persistSubtasks(embeddedSubtasks.map(s => s.id === id ? { ...s, title } : s));
  }
  function removeSubtask(id: string) {
    persistSubtasks(embeddedSubtasks.filter(s => s.id !== id));
  }

  const watchers = task.watcherIds || [];
  const isWatching = watchers.includes(state.user.id);
  const activeProjects = state.projects.filter(p => !p.archived);

  const subtasksDone = embeddedSubtasks.filter(s => s.completed).length;
  const checklistTotal = (task.checklist || []).length;
  const checklistDone = (task.checklist || []).filter(c => c.done).length;

  const prevId = siblings ? siblings.previousTaskId(task.id) : null;
  const nextId = siblings ? siblings.nextTaskId(task.id) : null;
  const hasArrows = !!siblings && (prevId !== null || nextId !== null);

  return (
    <>
      {/* Header row — arrows + close */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '10px 14px', borderBottom: `1px solid ${theme.border}`,
          flexShrink: 0,
        }}
      >
        {hasArrows && (
          <>
            <button
              onClick={() => prevId && dispatch({ type: 'SELECT_PROJECT_TASK', id: prevId })}
              disabled={!prevId}
              title="Previous task (↑)"
              style={iconBtnStyle(!!prevId)}
              aria-label="Previous task"
            >↑</button>
            <button
              onClick={() => nextId && dispatch({ type: 'SELECT_PROJECT_TASK', id: nextId })}
              disabled={!nextId}
              title="Next task (↓)"
              style={iconBtnStyle(!!nextId)}
              aria-label="Next task"
            >↓</button>
          </>
        )}
        <div style={{ flex: 1, fontSize: 11, color: theme.textTertiary }}>
          {project ? `${project.icon} ${project.name}` : 'Standalone (no project)'}
        </div>
        <button
          onClick={onClose}
          title="Close (Esc)"
          style={{
            background: 'none', border: 'none', color: theme.textTertiary,
            cursor: 'pointer', fontSize: 18, padding: '2px 8px',
          }}
          aria-label="Close drawer"
        >×</button>
      </div>

      {/* Scrollable body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
        {!canEdit && (
          <div
            style={{
              fontSize: 11, color: theme.textTertiary, background: theme.input,
              border: `1px solid ${theme.border}`, borderRadius: 6,
              padding: '6px 10px', marginBottom: 12,
            }}
          >
            Read-only — you aren't the creator, an assignee, or an admin.
          </div>
        )}

        {/* Title — editable, with done-checkbox on the left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 0 }}>
          <button
            onClick={() => setStatus(task.status === 'done' ? 'todo' : 'done')}
            disabled={!canEdit}
            title={task.status === 'done' ? 'Mark incomplete' : 'Mark done'}
            style={{
              width: 22, height: 22, borderRadius: 6, flexShrink: 0,
              border: `2px solid ${task.status === 'done' ? statusColors.done : statusColors[task.status] ?? '#888'}`,
              background: task.status === 'done' ? statusColors.done : 'transparent',
              cursor: canEdit ? 'pointer' : 'default',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 10, color: '#fff',
            }}
          >{task.status === 'done' ? '✓' : ''}</button>
          <input
            value={titleDraft}
            onChange={e => setTitleDraft(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={e => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }}
            readOnly={!canEdit}
            placeholder="Task title"
            style={{
              flex: 1, fontSize: 18, fontWeight: 700, color: theme.text,
              background: 'transparent', border: 'none', outline: 'none',
              padding: '4px 0 8px', fontFamily: 'inherit',
              textDecoration: task.status === 'done' ? 'line-through' : 'none',
            }}
          />
        </div>

        {/* Field row — status / priority / project / dates / etc. */}
        <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 6, rowGap: 6, marginBottom: 16 }}>
          <FieldLabel>Status</FieldLabel>
          <select
            value={task.status}
            onChange={e => setStatus(e.target.value as TaskStatus)}
            disabled={!canEdit}
            style={fieldInputStyle(statusColors[task.status])}
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{statusLabels[s]}</option>
            ))}
          </select>

          <FieldLabel>Priority</FieldLabel>
          <select
            value={task.priority}
            onChange={e => setPriority(e.target.value as TaskPriority)}
            disabled={!canEdit}
            style={fieldInputStyle(priorityColors[task.priority])}
          >
            {PRIORITY_OPTIONS.map(p => (
              <option key={p} value={p}>{priorityIcons[p]} {p}</option>
            ))}
          </select>

          <FieldLabel>Assignees</FieldLabel>
          <AssigneePicker
            users={state.registeredUsers}
            selectedIds={assigneeIds}
            onChange={setAssignees}
            disabled={!canEdit}
          />

          <FieldLabel>Project</FieldLabel>
          <select
            value={task.projectId || ''}
            onChange={e => setProject(e.target.value || null)}
            disabled={!canEdit}
            style={fieldInputStyle()}
          >
            <option value="">— Standalone (no project) —</option>
            {activeProjects.map(p => (
              <option key={p.id} value={p.id}>{p.icon} {p.name}</option>
            ))}
          </select>

          <FieldLabel>Start</FieldLabel>
          <input
            type="date"
            value={(task.startDate || '').slice(0, 10)}
            onChange={e => setStartDate(e.target.value)}
            disabled={!canEdit}
            style={fieldInputStyle()}
          />

          <FieldLabel>Due</FieldLabel>
          <input
            type="date"
            value={(task.dueDate || '').slice(0, 10)}
            onChange={e => setDueDate(e.target.value)}
            disabled={!canEdit}
            style={fieldInputStyle()}
          />

          <FieldLabel>Estimate</FieldLabel>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <input
              type="number"
              min={0}
              step="0.25"
              value={task.estimatedHours ?? ''}
              onChange={e => setEstimate(e.target.value)}
              disabled={!canEdit}
              placeholder="hours"
              style={{ ...fieldInputStyle(), width: 100 }}
            />
            <span style={{ fontSize: 11, color: theme.textTertiary }}>
              · Logged {(task.actualHours || 0).toFixed(1)}h
            </span>
          </div>

          <FieldLabel>Story pts</FieldLabel>
          <input
            type="number"
            min={0}
            step={1}
            value={task.storyPoints ?? ''}
            onChange={e => setStoryPoints(e.target.value)}
            disabled={!canEdit}
            placeholder="points"
            style={{ ...fieldInputStyle(), width: 100 }}
          />

          <FieldLabel>Labels</FieldLabel>
          <LabelEditor
            labels={task.labels || []}
            canEdit={canEdit}
            onAdd={addLabel}
            onRemove={removeLabel}
          />

          <FieldLabel>Milestone</FieldLabel>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: theme.textSecondary }}>
            <input
              type="checkbox"
              checked={!!task.isMilestone}
              onChange={e => setMilestone(e.target.checked)}
              disabled={!canEdit}
            />
            ◆ Mark as milestone
          </label>

          <FieldLabel>Recurrence</FieldLabel>
          <div>
            <RecurrencePicker
              value={task.recurrence || null}
              onChange={setRecurrence}
              disabled={!canEdit}
            />
          </div>

          <FieldLabel>Watchers</FieldLabel>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Button sm onClick={toggleWatcher}>
              {isWatching ? '👁 Watching' : '👁‍🗨 Watch'}
            </Button>
            {watchers.length > 0 && (
              <AssigneeStack ids={watchers} size={20} max={5} />
            )}
          </div>
        </div>

        {/* Description */}
        <div style={{ marginBottom: 16 }}>
          <FieldLabel>Description</FieldLabel>
          <textarea
            value={descDraft}
            onChange={e => setDescDraft(e.target.value)}
            onBlur={commitDescription}
            readOnly={!canEdit}
            rows={4}
            placeholder="What's this task about? Markdown is fine."
            style={{
              width: '100%', boxSizing: 'border-box',
              padding: '8px 10px', borderRadius: 6,
              border: `1px solid ${theme.border}`, background: theme.input,
              color: theme.text, fontSize: 12, fontFamily: 'inherit',
              resize: 'vertical', minHeight: 80,
            }}
          />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 12, flexWrap: 'wrap' }}>
          <TabButton active={tab === 'subtasks'} onClick={() => setTab('subtasks')}>
            Subtasks ({subtasksDone}/{embeddedSubtasks.length})
          </TabButton>
          <TabButton active={tab === 'checklist'} onClick={() => setTab('checklist')}>
            Checklist ({checklistDone}/{checklistTotal})
          </TabButton>
          <TabButton active={tab === 'comments'} onClick={() => setTab('comments')}>
            Comments ({(task.comments || []).length})
          </TabButton>
          <TabButton active={tab === 'activity'} onClick={() => setTab('activity')}>
            Activity ({(task.activity || []).length})
          </TabButton>
          <TabButton active={tab === 'time'} onClick={() => setTab('time')}>
            Time ({(task.timeEntries || []).length})
          </TabButton>
          <TabButton active={tab === 'files'} onClick={() => setTab('files')}>
            Files ({(task.attachments || []).length})
          </TabButton>
        </div>

        {tab === 'subtasks' && (
          <SubtasksTab
            subtasks={embeddedSubtasks}
            canEdit={canEdit}
            onAdd={addEmbeddedSubtask}
            onToggle={toggleSubtask}
            onRename={renameSubtask}
            onRemove={removeSubtask}
          />
        )}
        {tab === 'checklist' && (
          <ChecklistTab
            task={task}
            canEdit={canEdit}
            projectId={projectId}
          />
        )}
        {tab === 'comments' && (
          <CommentsTab
            task={task}
            canEdit={canEdit}
            draft={commentDraft}
            setDraft={setCommentDraft}
            onPost={postComment}
          />
        )}
        {tab === 'activity' && <ActivityTab task={task} />}
        {tab === 'time' && (
          <TimeTab task={task} canEdit={canEdit} projectId={projectId} />
        )}
        {tab === 'files' && (
          <FilesTab task={task} />
        )}

        {/* Footer meta */}
        <div
          style={{
            marginTop: 24, paddingTop: 12, borderTop: `1px solid ${theme.border}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: 10, color: theme.textTertiary,
          }}
        >
          <span>
            Created {new Date(task.createdAt).toLocaleString()}
            {' · '}
            Updated {new Date(task.updatedAt).toLocaleString()}
          </span>
          {canEdit && (
            <Button sm onClick={deleteTask}>🗑 Delete</Button>
          )}
        </div>
      </div>
    </>
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────
function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <div style={{ fontSize: 10, color: theme.textTertiary, fontWeight: 700, paddingTop: 6 }}>
      {String(children).toUpperCase()}
    </div>
  );
}

function fieldInputStyle(color?: string) {
  return {
    padding: '6px 8px', borderRadius: 6,
    border: `1px solid ${color ? `${color}55` : theme.border}`,
    background: theme.input, color: color || theme.text,
    fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' as const,
  };
}

function iconBtnStyle(enabled: boolean) {
  return {
    background: 'none', border: `1px solid ${theme.border}`,
    color: enabled ? theme.textSecondary : theme.textTertiary,
    cursor: enabled ? 'pointer' : 'not-allowed',
    fontSize: 12, padding: '2px 8px', borderRadius: 6,
    opacity: enabled ? 1 : 0.4,
  };
}

function LabelEditor({
  labels, canEdit, onAdd, onRemove,
}: {
  labels: string[];
  canEdit: boolean;
  onAdd: (label: string) => void;
  onRemove: (label: string) => void;
}) {
  const [draft, setDraft] = useState('');
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {labels.map(l => (
        <span
          key={l}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: 4,
            background: `${theme.accent}22`, border: `1px solid ${theme.accent}55`,
            color: theme.text, borderRadius: 12, padding: '1px 8px',
            fontSize: 11,
          }}
        >
          {l}
          {canEdit && (
            <button
              onClick={() => onRemove(l)}
              style={{ background: 'none', border: 'none', color: theme.textTertiary, cursor: 'pointer', fontSize: 12, padding: 0 }}
              aria-label={`Remove ${l}`}
            >×</button>
          )}
        </span>
      ))}
      {canEdit && (
        <input
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onAdd(draft);
              setDraft('');
            }
          }}
          placeholder={labels.length === 0 ? '+ Add label…' : '+'}
          style={{
            flex: '0 1 120px', minWidth: 80, padding: '4px 8px',
            borderRadius: 6, border: `1px solid ${theme.border}`,
            background: theme.input, color: theme.text,
            fontSize: 11, fontFamily: 'inherit',
          }}
        />
      )}
    </div>
  );
}

// ─── Tabs ────────────────────────────────────────────────────────────────────
function SubtasksTab({
  subtasks, canEdit, onAdd, onToggle, onRename, onRemove,
}: {
  subtasks: Subtask[];
  canEdit: boolean;
  onAdd: (title: string) => void;
  onToggle: (id: string) => void;
  onRename: (id: string, title: string) => void;
  onRemove: (id: string) => void;
}) {
  const [draft, setDraft] = useState('');
  return (
    <div>
      {subtasks.map(st => (
        <div key={st.id} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 0' }}>
          <input
            type="checkbox"
            checked={st.completed}
            disabled={!canEdit}
            onChange={() => onToggle(st.id)}
          />
          <input
            value={st.title}
            onChange={e => onRename(st.id, e.target.value)}
            readOnly={!canEdit}
            style={{
              flex: 1, fontSize: 12, padding: '2px 4px', background: 'transparent',
              border: 'none', borderBottom: `1px dashed ${theme.border}`,
              color: theme.text, fontFamily: 'inherit',
              textDecoration: st.completed ? 'line-through' : 'none',
            }}
          />
          {canEdit && (
            <button
              onClick={() => onRemove(st.id)}
              style={{ background: 'none', border: 'none', color: theme.textTertiary, cursor: 'pointer', fontSize: 13 }}
              aria-label="Delete subtask"
            >×</button>
          )}
        </div>
      ))}
      {subtasks.length === 0 && (
        <div style={{ fontSize: 11, color: theme.textTertiary, padding: '6px 0' }}>
          No subtasks yet.
        </div>
      )}
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => {
              if (e.key === 'Enter') {
                e.preventDefault();
                onAdd(draft);
                setDraft('');
              }
            }}
            placeholder="Add a subtask…"
            style={{
              flex: 1, padding: '6px 10px', borderRadius: 6,
              border: `1px solid ${theme.border}`, background: theme.input,
              color: theme.text, fontSize: 11, fontFamily: 'inherit',
            }}
          />
          <Button
            sm
            primary
            onClick={() => { onAdd(draft); setDraft(''); }}
            disabled={!draft.trim()}
          >+ Add</Button>
        </div>
      )}
    </div>
  );
}

function ChecklistTab({
  task, canEdit, projectId,
}: {
  task: Task;
  canEdit: boolean;
  projectId: string | null;
}) {
  const { dispatch } = useApp();
  const [draft, setDraft] = useState('');
  const items = task.checklist || [];
  function add() {
    if (!draft.trim()) return;
    dispatch({ type: 'ADD_TASK_CHECKLIST', taskId: task.id, projectId, text: draft.trim() });
    setDraft('');
  }
  return (
    <div>
      {items.map(item => (
        <div key={item.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
          <input
            type="checkbox"
            checked={item.done}
            disabled={!canEdit}
            onChange={() => dispatch({ type: 'TOGGLE_TASK_CHECKLIST', taskId: task.id, projectId, itemId: item.id })}
          />
          <span
            style={{
              flex: 1, fontSize: 12,
              textDecoration: item.done ? 'line-through' : 'none',
              color: item.done ? theme.textTertiary : theme.text,
            }}
          >{item.text}</span>
          {item.doneBy && (
            <span style={{ fontSize: 9, color: theme.textTertiary }}>by {getUserName(item.doneBy)}</span>
          )}
          {canEdit && (
            <button
              onClick={() => dispatch({ type: 'REMOVE_TASK_CHECKLIST', taskId: task.id, projectId, itemId: item.id })}
              style={{ background: 'none', border: 'none', color: theme.textTertiary, cursor: 'pointer', fontSize: 11 }}
              aria-label="Delete checklist item"
            >×</button>
          )}
        </div>
      ))}
      {items.length === 0 && (
        <div style={{ fontSize: 11, color: theme.textTertiary, padding: '6px 0' }}>
          No checklist items yet.
        </div>
      )}
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
            placeholder="Add a checklist item…"
            style={{
              flex: 1, padding: '6px 10px', borderRadius: 6,
              border: `1px solid ${theme.border}`, background: theme.input,
              color: theme.text, fontSize: 11, fontFamily: 'inherit',
            }}
          />
          <Button sm primary onClick={add} disabled={!draft.trim()}>+</Button>
        </div>
      )}
    </div>
  );
}

function CommentsTab({
  task, canEdit, draft, setDraft, onPost,
}: {
  task: Task;
  canEdit: boolean;
  draft: string;
  setDraft: (v: string) => void;
  onPost: () => void;
}) {
  const comments = task.comments || [];
  return (
    <div>
      {comments.map((c, i) => (
        <div key={i} style={{ display: 'flex', gap: 8, padding: '6px 0', borderBottom: `1px solid ${theme.border}` }}>
          <Avatar name={getUserName(c.by)} size={20} id={c.by} />
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 10, color: theme.textTertiary }}>
              <strong style={{ color: theme.text }}>{getUserName(c.by)}</strong> · {new Date(c.at).toLocaleString()}
            </div>
            <div style={{ fontSize: 12, color: theme.textSecondary, marginTop: 2, whiteSpace: 'pre-wrap' }}>{c.t}</div>
          </div>
        </div>
      ))}
      {comments.length === 0 && (
        <div style={{ fontSize: 11, color: theme.textTertiary, padding: '6px 0' }}>No comments yet.</div>
      )}
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8 }}>
          <input
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter' && draft.trim()) { e.preventDefault(); onPost(); } }}
            placeholder="Add a comment… (Enter to post)"
            style={{
              flex: 1, padding: '6px 10px', borderRadius: 6,
              border: `1px solid ${theme.border}`, background: theme.input,
              color: theme.text, fontSize: 11, fontFamily: 'inherit',
            }}
          />
          <Button sm primary onClick={onPost} disabled={!draft.trim()}>Post</Button>
        </div>
      )}
    </div>
  );
}

function ActivityTab({ task }: { task: Task }) {
  const entries = (task.activity || []).slice().reverse();
  if (entries.length === 0) {
    return (
      <div style={{ fontSize: 11, color: theme.textTertiary, padding: '6px 0' }}>
        No activity yet.
      </div>
    );
  }
  return (
    <div>
      {entries.map(a => (
        <div key={a.id} style={{ display: 'flex', gap: 8, padding: '4px 0', fontSize: 11, color: theme.textSecondary }}>
          <Avatar name={getUserName(a.userId)} size={16} id={a.userId} />
          <span style={{ flex: 1 }}>
            <strong style={{ color: theme.text }}>{getUserName(a.userId)}</strong> {a.text}
          </span>
          <span style={{ color: theme.textTertiary, fontSize: 9 }}>
            {new Date(a.at).toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
}

function TimeTab({
  task, canEdit, projectId,
}: {
  task: Task;
  canEdit: boolean;
  projectId: string | null;
}) {
  const { dispatch } = useApp();
  const [minutes, setMinutes] = useState('');
  const [note, setNote] = useState('');
  const [billable, setBillable] = useState(false);
  const entries = task.timeEntries || [];
  function log() {
    const m = parseFloat(minutes);
    if (!m || m <= 0) return;
    dispatch({ type: 'LOG_TASK_TIME', taskId: task.id, projectId, minutes: m, note, billable });
    setMinutes(''); setNote(''); setBillable(false);
  }
  return (
    <div>
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 10, flexWrap: 'wrap' }}>
          <input
            type="number" min={1}
            placeholder="Minutes"
            value={minutes}
            onChange={e => setMinutes(e.target.value)}
            style={{
              width: 100, padding: '6px 10px', borderRadius: 6,
              border: `1px solid ${theme.border}`, background: theme.input,
              color: theme.text, fontSize: 11,
            }}
          />
          <input
            placeholder="What did you work on?"
            value={note}
            onChange={e => setNote(e.target.value)}
            style={{
              flex: 1, minWidth: 160, padding: '6px 10px', borderRadius: 6,
              border: `1px solid ${theme.border}`, background: theme.input,
              color: theme.text, fontSize: 11,
            }}
          />
          <label style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 10, color: theme.textTertiary }}>
            <input type="checkbox" checked={billable} onChange={e => setBillable(e.target.checked)} /> Billable
          </label>
          <Button sm primary onClick={log} disabled={!minutes}>⏱ Log</Button>
        </div>
      )}
      {entries.map(e => (
        <div key={e.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 11, borderBottom: `1px solid ${theme.border}` }}>
          <Badge sm color={e.billable ? theme.green : theme.textTertiary}>{e.minutes}m</Badge>
          <span style={{ flex: 1 }}>{e.note || '—'}</span>
          <span style={{ color: theme.textTertiary, fontSize: 10 }}>
            {getUserName(e.userId)} · {e.workDate}
          </span>
          {canEdit && (
            <button
              onClick={() => dispatch({ type: 'DELETE_TASK_TIME', taskId: task.id, projectId, entryId: e.id })}
              style={{ background: 'none', border: 'none', color: theme.textTertiary, cursor: 'pointer' }}
              aria-label="Delete time entry"
            >×</button>
          )}
        </div>
      ))}
      {entries.length === 0 && (
        <div style={{ fontSize: 11, color: theme.textTertiary, padding: '6px 0' }}>No time entries yet.</div>
      )}
    </div>
  );
}

function FilesTab({ task }: { task: Task }) {
  const files = task.attachments || [];
  if (files.length === 0) {
    return (
      <div style={{ fontSize: 11, color: theme.textTertiary, padding: '6px 0' }}>
        No files attached. File attachment UI is coming in a follow-up.
      </div>
    );
  }
  return (
    <div>
      {files.map((f, i) => (
        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 11 }}>
          <span>📎</span>
          {(f as any).url ? (
            <a href={(f as any).url} target="_blank" rel="noreferrer" style={{ color: theme.accent }}>
              {(f as any).name || (f as any).url}
            </a>
          ) : (
            <span>{(f as any).name || 'attachment'}</span>
          )}
        </div>
      ))}
    </div>
  );
}
